import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  Button,
  Divider,
  DividerVariants,
  Iconography,
  IconographySizes,
  IconographyThemes,
  Loader,
  LoaderSizes,
  Modal,
} from "@hid-galaxy-ui/galaxy-react";
import moment from "moment";
import sanitizeHtml from "sanitize-html";
import {
  addPartnerSagaAction,
  getAllPartnersSagaAction,
} from "../../../../redux/saga/partner-details/saga-actions";
import { useAppDispatch, useAppSelector } from "../../../../redux";
import { Constants } from "../../../../utils/constants";
import { PartnerFormData } from "../../../../hooks/usePartnerForm";
import PartnerDetailForm from "../partner-detail-form";
import "./CreateNewPartnerModal.scss";

const INITIAL_PARTNER_DETAILS: PartnerFormData = {
  name: "",
  email: "",
  accountManagerEmail: "",
  type: [],
  potential: "Prime",
  website: "",
  description: "",
  region: Constants.REGIONS[0],
  revenueStream: "",
  partnerVertical: [],
  partnerTier: "",
  country: "",
  state: "",
  countryCode: "",
  phoneNumber: "",
  streetAddress: "",
  registrationDate: null,
};

type CreateNewPartnerModalProps = {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
};

const ModalHeaderSubtitle: React.FC<{ text: string }> = ({ text }) => {
  const [headlineEl, setHeadlineEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setHeadlineEl(
      document.querySelector(
        ".create-new-partner-modal .hid-modal__header-headline"
      ) as HTMLElement | null
    );
  }, [text]);

  if (!headlineEl) {
    return null;
  }

  return createPortal(
    <p className="create-new-partner-modal__subtitle">{text}</p>,
    headlineEl
  );
};

const CreateNewPartnerModal: React.FC<CreateNewPartnerModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [step, setStep] = useState<1 | 2>(1);
  const [partnerDetails, setPartnerDetails] =
    useState<PartnerFormData>(INITIAL_PARTNER_DETAILS);
  const [formSubmitted, setFormSubmitted] = useState(false);

  const { loggedUserDetail } = useAppSelector((state) => state.auth);
  const { status } = useAppSelector((state) => state.partner_details);
  const dispatch = useAppDispatch();

  const emailRegex = useMemo(
    () => /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
    []
  );

  const resetModalState = useCallback(() => {
    setStep(1);
    setPartnerDetails(INITIAL_PARTNER_DETAILS);
    setFormSubmitted(false);
  }, []);

  const handleClose = useCallback(() => {
    resetModalState();
    onClose();
  }, [onClose, resetModalState]);

  useEffect(() => {
    if (!open) {
      resetModalState();
    }
  }, [open, resetModalState]);

  useEffect(() => {
    if (!open) {
      return;
    }
    document.body.classList.add("create-new-partner-modal-open");
    return () => {
      document.body.classList.remove("create-new-partner-modal-open");
    };
  }, [open]);

  const formatDateForDB = useCallback((date: Date | null) => {
    if (date) {
      return date.toLocaleDateString("en-CA");
    }
    return null;
  }, []);

  const checkEmptyString = useCallback((value: string) => {
    return value.length > 0 ? value : "—";
  }, []);

  const tableValue = useMemo(
    () => [
      { label: "Email ID", value: partnerDetails.email },
      {
        label: "HID Account Manager Email ID",
        value: partnerDetails.accountManagerEmail,
      },
      { label: "Corporate Website", value: partnerDetails.website },
      {
        label: "Phone Number",
        value: partnerDetails.countryCode + partnerDetails.phoneNumber,
      },
      { label: "Description", value: partnerDetails.description },
      { label: "Partner Type", value: partnerDetails.type.join(", ") },
      { label: "Region", value: partnerDetails.region },
      { label: "Revenue Stream", value: partnerDetails.revenueStream },
      {
        label: "Partner Vertical",
        value: partnerDetails.partnerVertical.join(", "),
      },
      { label: "Partner Tier", value: partnerDetails.partnerTier },
      {
        label: "Registration Date",
        value: partnerDetails.registrationDate
          ? moment(partnerDetails.registrationDate).format("DD-MMM-YYYY")
          : "",
      },
    ],
    [partnerDetails]
  );

  const isNextButtonEnabled = useMemo(
    () =>
      Boolean(partnerDetails.name?.trim()) &&
      (!partnerDetails.accountManagerEmail ||
        emailRegex.test(partnerDetails.accountManagerEmail)) &&
      (!partnerDetails.email || emailRegex.test(partnerDetails.email)),
    [
      partnerDetails.name,
      partnerDetails.accountManagerEmail,
      partnerDetails.email,
      emailRegex,
    ]
  );

  const handleNext = useCallback(() => {
    setStep(2);
  }, []);

  const handleBack = useCallback(() => {
    setStep(1);
  }, []);

  const handleCreatePartner = useCallback(() => {
    const sanitizedDescription = sanitizeHtml(partnerDetails.description);
    dispatch(
      addPartnerSagaAction({
        partner_name: partnerDetails.name,
        description: sanitizedDescription,
        account_manager: partnerDetails.accountManagerEmail,
        partner_type: partnerDetails.type.join(","),
        partner_potential: partnerDetails.potential,
        partner_email: partnerDetails.email,
        website: partnerDetails.website,
        region: partnerDetails.region,
        revenue_stream: partnerDetails.revenueStream,
        partner_vertical: partnerDetails.partnerVertical.join(","),
        partner_tier: partnerDetails.partnerTier,
        country: partnerDetails.country,
        state: partnerDetails.state,
        company_contact: partnerDetails.countryCode + partnerDetails.phoneNumber,
        street_address: partnerDetails.streetAddress,
        initiatedBy: loggedUserDetail.email,
        submitted_by: loggedUserDetail?.ID,
        registration_date: formatDateForDB(partnerDetails.registrationDate),
      })
    );
    setFormSubmitted(true);
  }, [partnerDetails, loggedUserDetail, formatDateForDB, dispatch]);

  const handleContinueAfterSuccess = useCallback(() => {
    dispatch(getAllPartnersSagaAction());
    onSuccess?.();
    handleClose();
  }, [dispatch, onSuccess, handleClose]);

  const footerConfig = useMemo(() => {
    if (!formSubmitted || status.addPartnerLoading) {
      return undefined;
    }
    return {
      primaryBtnLabel: "CONTINUE",
      onClickPrimaryBtn: handleContinueAfterSuccess,
    };
  }, [
    formSubmitted,
    status.addPartnerLoading,
    handleContinueAfterSuccess,
  ]);

  const renderStepFooter = () => (
    <div className="create-new-partner-modal__footer">
      {step === 1 ? (
        <>
          <Button variant="secondary" label="CANCEL" onClick={handleClose} />
          <Button
            variant="primary"
            label="NEXT"
            disabled={!isNextButtonEnabled}
            onClick={handleNext}
          />
        </>
      ) : (
        <>
          <Button variant="secondary" label="BACK" onClick={handleBack} />
          <Button
            variant="primary"
            label="CREATE PARTNER"
            onClick={handleCreatePartner}
          />
        </>
      )}
    </div>
  );

  const headerSubtitle = useMemo(() => {
    if (formSubmitted) {
      return null;
    }
    return step === 1
      ? "Configure a new partner details"
      : "Review and confirm partner details";
  }, [formSubmitted, step]);

  if (!open) {
    return null;
  }

  return (
    <Modal
      optionalHeader="Partner Organizations"
      headerText="Add New Partner"
      width={920}
      className="create-new-partner-modal"
      onClose={handleClose}
      footerConfig={footerConfig}
    >
      {headerSubtitle ? <ModalHeaderSubtitle text={headerSubtitle} /> : null}
      {formSubmitted ? (
        <div className="create-new-partner-modal__status">
          {status.addPartnerLoading ? (
            <Loader variant={LoaderSizes.large} />
          ) : status.addPartnerStatus ? (
            <>
              <Iconography
                icon="circleCheck"
                size={IconographySizes.XLarge}
                theme={IconographyThemes.Success}
              />
              <div className="message">Partner Registered</div>
            </>
          ) : (
            <>
              <Iconography
                icon="circleXmark"
                size={IconographySizes.XLarge}
                theme={IconographyThemes.Error}
              />
              <div className="message">{status.addPartnerMessage}</div>
            </>
          )}
        </div>
      ) : step === 1 ? (
        <>
          <PartnerDetailForm
            layout="modal"
            partnerDetails={partnerDetails}
            setPartnerDetails={setPartnerDetails}
          />
          {renderStepFooter()}
        </>
      ) : (
        <div className="create-new-partner-modal__review">
          <div className="top">
            <div className="row">
              <div className="label-value">
                <label>Partner company name</label>
                <span>{partnerDetails.name}</span>
              </div>
            </div>
            <div className="row">
              <div className="label-value">
                <label>Street Address</label>
                <span>{checkEmptyString(partnerDetails.streetAddress)}</span>
              </div>
              <div className="label-value">
                <label>Country</label>
                <span>{checkEmptyString(partnerDetails.country)}</span>
              </div>
            </div>
          </div>
          <Divider mode={DividerVariants.Content} />
          <div className="detail-table">
            {tableValue.map((row) => (
              <div className="table-row" key={row.label}>
                <div className="label">{row.label}</div>
                {row.label === "Description" ? (
                  <div
                    className="value"
                    dangerouslySetInnerHTML={{
                      __html: checkEmptyString(row.value),
                    }}
                  />
                ) : (
                  <div className="value">{checkEmptyString(row.value)}</div>
                )}
              </div>
            ))}
          </div>
          {renderStepFooter()}
        </div>
      )}
    </Modal>
  );
};

export default CreateNewPartnerModal;
